/**
 * Types for workflow app API responses
 */

/**
 * Workflow App (Azure Container App with otto tag)
 * Returned from Azure Resource Graph
 */
export type LogicApp = {
  id: string;
  name: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
  /** FQDN of the ACA app for direct-plane API calls */
  defaultHostName?: string | undefined;
};

/**
 * Workflow within a Logic App
 */
export type Workflow = {
  id: string;
  name: string;
  type: string;
  kind?: 'Stateful' | 'Stateless' | 'Agent';
  /** Source app display name — populated when fetching across multiple apps */
  appName?: string | undefined;
  properties: {
    state?: 'Enabled' | 'Disabled';
    flowState?: 'Enabled' | 'Disabled';
    health?: {
      state: 'Healthy' | 'Unhealthy' | 'Unknown';
    };
    createdTime?: string;
    changedTime?: string;
    provisioningState?: string;
  };
};

/**
 * Workflows list API response
 */
export type WorkflowsResponse = {
  value: Workflow[];
  nextLink?: string;
};

/**
 * Connection reference within workflow configuration
 */
export type ConnectionReference = {
  connectionId: string;
  connectionName: string;
  id: string;
};

/**
 * Managed API connection
 */
export type ManagedApiConnection = {
  api: {
    id: string;
  };
  connection: {
    id: string;
  };
  connectionRuntimeUrl: string;
  authentication: {
    type: string;
  };
  /** Connector-specific runtime properties (e.g. azureAgentAIResourceName for azureagentservice) */
  connectionProperties?: Record<string, unknown>;
  /**
   * Optional user-facing display name. When omitted, UIs fall back to
   * the `connections.json` key (which may be opaque, e.g. `apiconnection-0576`,
   * for connections created outside the portal).
   *
   * Mirrors the `displayName` field on `ServiceProviderConnection`,
   * `AgentConnection`, and `McpConnection` so all connection families
   * share the same naming contract.
   */
  displayName?: string;
};

// Re-export from connections.ts for backwards compatibility
export type { ServiceProviderConnection } from './connections';

/**
 * Agent connection configuration
 * Represents a connection to an AI model provider (Azure OpenAI, etc.)
 */
export type AgentConnection = {
  /** Connection display name */
  displayName?: string;
  /** Model provider configuration */
  modelProvider?: {
    id: string;
    type?: string;
  };
  /** Model configuration details */
  modelConfiguration?: {
    deploymentName?: string;
    modelName?: string;
    apiVersion?: string;
  };
  /** Parameter values for the connection */
  parameterValues?: Record<string, unknown>;
  /** Authentication settings */
  authentication?: {
    type: string;
    identity?: string;
    key?: string;
  };
  /** Azure OpenAI endpoint URL */
  endpoint?: string;
  /** Azure resource ID */
  resourceId?: string;
  /** Connection type (e.g., "model") */
  type?: string;
};

/**
 * MCP connection configuration as persisted under
 * `connections.json.agentMcpConnections[<key>]`. The runtime resolves a BYO
 * MCP tool by `inputs.connectionReference.connectionName` against this map
 * (see the runtime's `AgentMcpConnection` / `EdgeConnectionsDetails`).
 *
 * `authentication` is intentionally permissive — its shape varies by mode
 * (None / Basic / Key / ManagedServiceIdentity) and is emitted as `null`
 * when unauthenticated, mirroring Logic Apps Standard. The portal validates
 * the concrete shape before persisting (see `McpAddServerForm`).
 */
export type McpConnection = {
  mcpServerUrl: string;
  displayName?: string;
  authentication?: Record<string, unknown> | null;
  parameterValues?: Record<string, unknown>;
};

/**
 * Connections configuration response
 */
export type ConnectionsConfiguration = {
  managedApiConnections?: Record<string, ManagedApiConnection>;
  serviceProviderConnections?: Record<string, import('./connections').ServiceProviderConnection>;
  agentConnections?: Record<string, AgentConnection>;
  agentMcpConnections?: Record<string, McpConnection>;
  hostedAgentConnections?: Record<string, import('./hostedAgent').HostedAgentConnection>;
};

/**
 * Connections response wrapper
 */
export type ConnectionsResponse = {
  properties: {
    files?: {
      'connections.json'?: ConnectionsConfiguration;
    };
    health?: {
      state: string;
    };
  };
};

/**
 * Flattened connection for display
 */
export type Connection = {
  name: string;
  apiId: string;
  connectionId: string;
  type: 'managedApi' | 'serviceProvider' | 'function' | 'agent' | 'mcp';
};

/**
 * Workflow definition response with files (when using $expand=connections.json)
 * Includes both production and draft files when $expand includes draft artifacts.
 */
export type WorkflowDefinitionFiles = {
  // Production files
  'workflow.json'?: {
    definition?: unknown;
    kind?: string;
  };
  'connections.json'?: ConnectionsConfiguration;
  'parameters.json'?: Record<string, unknown>;
  'notes.json'?: Record<string, unknown>;
  // Draft files (included when $expand includes draft artifacts)
  'workflow-draft.json'?: {
    definition?: unknown;
    kind?: string;
  };
  'connections-draft.json'?: ConnectionsConfiguration;
  'parameters-draft.json'?: Record<string, unknown>;
  'notes-draft.json'?: Record<string, unknown>;
};

export type WorkflowDefinitionResponse = {
  properties: {
    files?: WorkflowDefinitionFiles;
    flowState?: string;
    health?: { state: string };
  };
  id:string; 
  location:string;
  kind?:string;
  type?:string;
  name?:string;
};

/**
 * Parameters.json content
 */
export type ParametersFile = Record<string, ParameterDefinition>;

/**
 * Individual parameter definition
 */
export type ParameterDefinition = {
  type: string;
  value: unknown;
  metadata?: {
    description?: string;
  };
};

/**
 * Flattened parameter for display
 */
export type Parameter = {
  name: string;
  type: string;
  value: unknown;
  description?: string;
};

/**
 * Resource Graph query response
 */
export type ResourceGraphResponse = {
  totalRecords: number;
  count: number;
  data: LogicApp[];
};

/**
 * Parse app ID to extract subscription, resource group, and app name.
 * Supports ACA Container App ARM IDs.
 */
export const parseAppId = (
  appId: string
): { subscriptionId: string; resourceGroup: string; appName: string } | null => {
  if (typeof appId !== 'string') return null;
  const match = appId.match(
    /\/subscriptions\/([^/]+)\/resourceGroups\/([^/]+)\/providers\/Microsoft\.App\/containerApps\/([^/]+)/i
  );

  if (!match) {
    return null;
  }

  const [, subscriptionId, resourceGroup, appName] = match;
  return {
    subscriptionId: subscriptionId ?? '',
    resourceGroup: resourceGroup ?? '',
    appName: appName ?? '',
  };
};

/**
 * Parse workflow ID to extract the workflow name
 * Format: /subscriptions/{sub}/resourceGroups/{rg}/providers/Microsoft.App/containerApps/{app}/workflows/{workflowName}
 */
export const parseWorkflowId = (workflowId: string): string | null => {
  const match = workflowId.match(/\/workflows\/([^/]+)$/i);
  return match ? match[1] ?? null : null;
};

/**
 * Extract the display name from any Azure ARM resource ID or slash-delimited path.
 * Returns the last segment after the final '/'. Safe to call on bare names (returns unchanged).
 *
 * Examples:
 *   "/subscriptions/.../sites/myApp" → "myApp"
 *   "appName/workflowName"           → "workflowName"
 *   "bareString"                     → "bareString"
 */
export const extractResourceName = (idOrPath: string): string => {
  const lastSlash = idOrPath.lastIndexOf('/');
  return lastSlash === -1 ? idOrPath : idOrPath.slice(lastSlash + 1);
};

/**
 * Extract the workflow name from a potentially prefixed name.
 * ARM API returns names as "appName/workflowName", we need just "workflowName".
 * Safe to call on names that are already bare (returns unchanged).
 */
export const extractWorkflowName = extractResourceName;
